/**
 * LiveDemo — «بطاقتك تتكوّن أمامك»: one pass, three tap-switch steps (approved UX,
 * replaces the old scroll-pinned showcase). The desktop step list (.dsteps) and the
 * phone/tablet segmented control (.dseg) share one state.
 */
import React, { useState } from 'react';
import {
  AppleIcon, CheckmarkCircle02Icon, GoogleIcon, Image01Icon, PaintBrush02Icon, PencilEdit02Icon, Tick02Icon
} from '@hugeicons/core-free-icons';
import Icon from '../../components/v2/Icon.jsx';
import { FakeQR } from '../../components/WalletPass.jsx';
import { DEMO_STEP_KEYS } from './strings.js';
import { useStr } from './useStr.js';
import { C, ME, SWATCHES, PHOTO_URL, LOGO_URL, ICON_URL } from './data.js';
import PassSlot from './PassSlot.jsx';

const L = ({ children, className = '' }) => <span className={`ltr${className ? ` ${className}` : ''}`}>{children}</span>;

const STEP_ICONS = [
  { icon: PencilEdit02Icon, g: 'g2' },
  { icon: PaintBrush02Icon, g: 'g3' },
  { icon: Image01Icon, g: 'g1' }
];

const TYPED = 'Mohammed Al-Z';

/**
 * ARIA tabs keyboard pattern for both step lists: arrows move and select (Left/Right
 * follow the reading direction, Up/Down for the vertical desktop list), Home/End
 * jump to the first/last step, and focus follows the selected tab (roving tabindex).
 */
function onTabKey(e, i, ar, setStep) {
  const n = DEMO_STEP_KEYS.length;
  const delta = { ArrowDown: 1, ArrowUp: -1, ArrowRight: ar ? -1 : 1, ArrowLeft: ar ? 1 : -1 }[e.key];
  let to = null;
  if (delta) to = (i + delta + n) % n;
  else if (e.key === 'Home') to = 0;
  else if (e.key === 'End') to = n - 1;
  if (to == null) return;
  e.preventDefault();
  setStep(to);
  e.currentTarget.parentElement.querySelectorAll('[role="tab"]')[to]?.focus();
}

function demoCard(step, color) {
  if (step === 0) return { ...ME, ...C.white, full_name_en: <>{TYPED}<span className="caret" /></> };
  return { ...ME, ...C[color] };
}

export default function LiveDemo() {
  const { t, ar } = useStr();
  const [step, setStep] = useState(0);
  const [color, setColor] = useState('green');
  const card = demoCard(step, color);
  const withMedia = step === 2;

  return (
    <section className="sx">
      <div className="wrap">
        <div className="pg demo" id="demo">
          <div className="dtx">
            <div className="eb">{t('demoEb')}</div>
            <h2 className="h">{t('demoTitle')}</h2>
            <p className="ssub">{t('demoSub')}</p>
            <div className="dsteps" role="tablist" aria-label={t('demoSteps')} aria-orientation="vertical">
              {DEMO_STEP_KEYS.map((k, i) => (
                <button
                  key={k.t}
                  type="button"
                  className={`dst${step === i ? ' on' : ''}`}
                  data-step={i}
                  role="tab"
                  aria-selected={step === i}
                  aria-controls="dstage"
                  tabIndex={step === i ? 0 : -1}
                  onClick={() => setStep(i)}
                  onKeyDown={(e) => onTabKey(e, i, ar, setStep)}
                >
                  <span className={`ic-t ${STEP_ICONS[i].g}`}><Icon icon={STEP_ICONS[i].icon} /></span>
                  <span><b>{t(k.t)}</b><span className="ds">{t(k.d)}</span></span>
                  <span className="k ltr">{String(i + 1).padStart(2, '0')}</span>
                </button>
              ))}
            </div>
          </div>

          <div className="dvz">
            <div className="seg dseg" role="tablist" aria-label={t('demoSteps')}>
              {DEMO_STEP_KEYS.map((k, i) => (
                <button
                  key={k.seg}
                  type="button"
                  className={step === i ? 'on' : undefined}
                  data-step={i}
                  role="tab"
                  aria-selected={step === i}
                  aria-controls="dstage"
                  tabIndex={step === i ? 0 : -1}
                  onClick={() => setStep(i)}
                  onKeyDown={(e) => onTabKey(e, i, ar, setStep)}
                >
                  {t(k.seg)}
                </button>
              ))}
            </div>
            <p className="dhint" aria-live="polite">{t(DEMO_STEP_KEYS[step].d)}</p>
            <div className="dstage" id="dstage" role="tabpanel">
              <span className="live"><i />{t('live')}</span>
              <div className="dctl">
                <div className={step === 0 ? 'on' : undefined} data-ctl="0">
                  <div className="in"><L>{TYPED}<span className="caret" /></L></div>
                </div>
                <div className={`swb${step === 1 ? ' on' : ''}`} data-ctl="1">
                  {SWATCHES.map((s) => (
                    <button
                      key={s.key}
                      type="button"
                      data-c={s.key}
                      className={color === s.key ? 'on' : undefined}
                      aria-label={t(s.label)}
                      aria-pressed={color === s.key}
                      onClick={() => setColor(s.key)}
                    >
                      <i style={{ background: C[s.key].bg_color }} />
                    </button>
                  ))}
                </div>
                <div className={`ups${step === 2 ? ' on' : ''}`} data-ctl="2">
                  <div className="up"><img className="lg" src={ICON_URL} alt="" />{t('upLogo')}<Icon icon={CheckmarkCircle02Icon} /></div>
                  <div className="up"><img src={PHOTO_URL} alt="" />{t('upPhoto')}<Icon icon={CheckmarkCircle02Icon} /></div>
                </div>
              </div>
              <PassSlot
                id="dpass"
                card={card}
                logoUrl={withMedia ? LOGO_URL : undefined}
                photoUrl={withMedia ? PHOTO_URL : undefined}
                scale={0.92}
                scaleT={0.92}
                scaleM={0.9}
                fit
              />
            </div>
          </div>

          <div className="ready">
            <span className="ck"><Icon icon={Tick02Icon} /></span>
            <div className="tx">
              <small>{t('ready')}</small>
              <b>{ar
                ? <>بطاقتك جاهزة في أقل من <span className="nw"><L className="num">30</L> ثانية.</span></>
                : <>Your card is ready in under <span className="nw">30 seconds.</span></>}</b>
              <span>{ar
                ? <>أضفها لمحفظتك، ومن يصوّر <span className="nw">الـ <L>QR</L></span> تنحفظ بياناتك عنده.</>
                : 'Add it to your wallet; anyone who scans the QR saves your details.'}</span>
            </div>
            <div className="end">
              <div className="qrm"><div className="qt"><FakeQR /></div>{t('scanSave')}</div>
              <div className="works">
                <small>{t('worksWith')}</small>
                <div className="wm">
                  <span className="ltr"><Icon icon={AppleIcon} />Apple Wallet</span>
                  <span className="ltr"><Icon icon={GoogleIcon} />Google Wallet</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
